import React, { use, useCallback, useMemo, useState } from "react";
import { useNavigation } from "@react-navigation/native";
import ScreenCard from "./ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../navigations/RootNavigation";
import { useCurrencyFormatter, useI18n } from "../hooks/i18n-hooks";
import { useTheme } from "../hooks/use-theme";
import ListItem from "../ui_atoms/ListItem";
import { StyleSheet } from "react-native";
import ToggleView from "../ui_atoms/ToggleView";
import Gap from "../ui_atoms/Gap";
import { EarningType, ProfileType, SalaryDeductionType } from "../__generated__/graphql";
import { BodySmallText, TitleMediumText } from "../components/FormattedText";
import { ProfileSwitcherManagerContext } from "../contexts/ProfileSwitcherManager";
import { BubbleTalk } from "../components/BubbleTalk";
import DocuIllSvg from "../assets/images/docu_ill.svg";
import VStack from "../ui_atoms/VStack";
import LabelValueText from "../components/LabelValueText";
import { useIntercom } from "../hooks/intercom";
import { useCurrentProfile } from "../hooks/use-current-profile";
import { getPaidSalary } from "../utils";

type SalaryCountingDetailsScreenProps = StackScreenProps<
    RootStackParamList,
    "SalaryCountingDetails"
>;

const VAT_PERCENT = 8.1; // will be change to use a value from the server
const LIFT_MONTHLY_FEE_PER_EMPLOYEE = 1900; // 19 CHF, will be change to use a value from the server
const SalaryCountingDetailsScreen = ({ route }: SalaryCountingDetailsScreenProps) => {
    const { salaryDetails } = route.params;
    const { profileType } = use(ProfileSwitcherManagerContext);
    const currentProfile = useCurrentProfile();
    const i18n = useI18n();
    const styles = useStyles();
    const { colors, fonts } = useTheme();
    const navigation = useNavigation();
    const currencyFormatter = useCurrencyFormatter();
    const { presentArticleId } = useIntercom();

    const [isClientDeductionsDetailsVisible, setIsClientDeductionsDetailsVisible] = useState(false);
    const [isWorkerDeductionsDetailsVisible, setIsWorkerDeductionsDetailsVisible] = useState(false);
    const [isCompensationsDetailsVisible, setIsCompensationsDetailsVisible] = useState(false);

    const onClientDeductionsDetailsPress = useCallback(() => {
        setIsClientDeductionsDetailsVisible(!isClientDeductionsDetailsVisible);
    }, [isClientDeductionsDetailsVisible]);

    const onCompensationsDetailsPress = useCallback(() => {
        setIsCompensationsDetailsVisible(!isCompensationsDetailsVisible);
    }, [isCompensationsDetailsVisible]);

    const onWorkerDeductionsDetailsPress = useCallback(() => {
        setIsWorkerDeductionsDetailsVisible(!isWorkerDeductionsDetailsVisible);
    }, [isWorkerDeductionsDetailsVisible]);

    // Renders a single deduction row. `indented` is used for items revealed inside a
    // "Détails" toggle; top-level rows (e.g. monthly charges) stay flush-left.
    const renderDeduction = useCallback(
        (deduction: SalaryDeductionType, indented = true) => {
            if (deduction.amount === 0) {
                return null;
            }
            return (
                <LabelValueText
                    key={deduction.title}
                    isSingleLine
                    textStyle={{
                        ...(indented ? { marginLeft: 10 } : null),
                        alignContent: "center",
                        flexShrink: 1,
                        marginTop: 0,
                    }}
                    label={deduction.i18nValue || "?"}
                    value={currencyFormatter({
                        amountInCents: deduction.amount,
                    })}
                    onInfo={
                        deduction.intercomArticleId
                            ? () => {
                                  void presentArticleId(deduction.intercomArticleId as string);
                              }
                            : undefined
                    }
                />
            );
        },
        [currencyFormatter, presentArticleId],
    );

    const formatItem = useCallback(
        (deduction: SalaryDeductionType) => renderDeduction(deduction),
        [renderDeduction],
    );

    // Monthly charges are listed inline within a single left-aligned sentence, with each
    // amount shown in the text itself (no right-aligned value column).
    const monthlyDeductionsSentence = useCallback(
        (introKey: string, items: { label: string; amount: number }[]) =>
            `${i18n.t(introKey)}\n${items
                .map(
                    (item) =>
                        `${item.label} = ${currencyFormatter({ amountInCents: item.amount })}`,
                )
                .join("\n")}`,
        [i18n, currencyFormatter],
    );

    const totalCompensation = useMemo(
        () =>
            (salaryDetails.vacationCompensation || 0) +
            (salaryDetails.publicHolidaysCompensation || 0) +
            (salaryDetails.thirteenMonthCompensation || 0),
        [
            salaryDetails.publicHolidaysCompensation,
            salaryDetails.thirteenMonthCompensation,
            salaryDetails.vacationCompensation,
        ],
    );

    const isNonMonthly = route.params.earningType !== EarningType.Monthly;

    // Hourly/daily vacation indemnity held as a provision ("cagnotte") instead of being paid with
    // the salary: the worker part then takes it out of the gross before the deductions.
    const isVacationIndemnityHeldBack =
        [EarningType.Hourly, EarningType.Daily].includes(route.params.earningType) &&
        !route.params.payVacationIndemnityWithSalary;

    // A monthly 13x contract holds its 13th share back the same way, paid on release.
    const isThirteenthSalaryHeldBack =
        route.params.earningType === EarningType.Monthly && !route.params.pay13thSalaryWithSalary;

    // At most one indemnity is held back: the vacation one (hourly/daily) or the 13th (monthly).
    const heldBack = isVacationIndemnityHeldBack
        ? {
              labelKey: "vacancy_indemnity",
              amount: salaryDetails.vacationCompensation,
              workerNoteKey: "worker_vacation_note",
              employerNoteKey: "employer_vacation_note",
          }
        : isThirteenthSalaryHeldBack
          ? {
                labelKey: "thirteenth_provision",
                amount: salaryDetails.thirteenMonthCompensation,
                workerNoteKey: "worker_13th_note",
                employerNoteKey: "employer_13th_note",
            }
          : null;

    // Under a non-LSE (co-employment) legal employer the client is not invoiced: it pays each
    // period's payslip itself, salary paid out plus employer charges. A held-back indemnity
    // (vacation or 13th) is paid on release, with its charges, so it is out of this period.
    const isClientPayingThePeriod = !route.params.useLseCct;

    // For non-monthly missions, deductions flagged appliedMonthly are billed once per month
    // rather than per mission period, so they are shown separately from the per-period lists.
    const displayedClientDeductions = useMemo(
        () =>
            (salaryDetails.clientDeductions || []).filter(
                (d) => !isNonMonthly || !d.appliedMonthly,
            ),
        [salaryDetails.clientDeductions, isNonMonthly],
    );

    const displayedWorkerDeductions = useMemo(
        () =>
            (salaryDetails.workerDeductions || []).filter(
                (d) => !isNonMonthly || !d.appliedMonthly,
            ),
        [salaryDetails.workerDeductions, isNonMonthly],
    );

    const clientMonthlyDeductions = useMemo(
        () =>
            isNonMonthly
                ? (salaryDetails.clientDeductions || []).filter(
                      (d) => d.appliedMonthly && d.amount !== 0,
                  )
                : [],
        [salaryDetails.clientDeductions, isNonMonthly],
    );

    const workerMonthlyDeductions = useMemo(
        () =>
            isNonMonthly
                ? (salaryDetails.workerDeductions || []).filter(
                      (d) => d.appliedMonthly && d.amount !== 0,
                  )
                : [],
        [salaryDetails.workerDeductions, isNonMonthly],
    );

    const totalClientDeductions = useMemo(
        () => displayedClientDeductions.reduce((result, d) => result + d.amount, 0),
        [displayedClientDeductions],
    );

    const totalWorkerDeductions = useMemo(
        () => displayedWorkerDeductions.reduce((result, d) => result + d.amount, 0),
        [displayedWorkerDeductions],
    );

    const vatToUse = useMemo(() => {
        if (currentProfile?.client?.useLegalEmployer.useLseCct) {
            return VAT_PERCENT;
        }
        return 0;
    }, [currentProfile?.client?.useLegalEmployer.useLseCct]);

    return (
        <>
            <VStack h="2/3" bottom="2/3">
                <BubbleTalk
                    personImageComponent={<DocuIllSvg width="100%" height="100%" />}
                    personSize={44}
                    position="center-right"
                    bubbleBackgroundColor={colors.accentLighter}
                >
                    <TitleMediumText>
                        {i18n.t("screens.user.salary_counting_details.title")}
                    </TitleMediumText>
                </BubbleTalk>
            </VStack>
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("screens.user.salary_counting_details.close"),
                        onPress: () => navigation.goBack(),
                        mode: "outlined",
                    },
                ]}
            >
                <VStack h="2/3" gap={0}>
                    {/* base salary */}
                    <LabelValueText
                        label={i18n.t("screens.user.salary_counting_details.base_salary")}
                        value={currencyFormatter({
                            amountInCents: salaryDetails.baseSalary,
                        })}
                    />
                    {[EarningType.Hourly, EarningType.Daily].includes(route.params.earningType) && (
                        <>
                            <LabelValueText
                                label={i18n.t(
                                    "screens.user.salary_counting_details.holiday_13th_compensations",
                                )}
                                value={currencyFormatter({
                                    amountInCents: totalCompensation,
                                })}
                            />
                            <ListItem
                                inGroup
                                style={styles.details_text}
                                labels={[
                                    {
                                        text: i18n.t(
                                            `screens.user.salary_counting_details.${
                                                isCompensationsDetailsVisible ? "hide" : "show"
                                            }_details`,
                                        ),
                                        mode: "miniCta",
                                    },
                                ]}
                                onPress={onCompensationsDetailsPress}
                                rightActionMode="none"
                            />

                            <ToggleView isOpen={isCompensationsDetailsVisible}>
                                <VStack gap="1/3">
                                    {[
                                        {
                                            title: "thirteen_month_compensation",
                                            quantity: 1,
                                            amount: salaryDetails.thirteenMonthCompensation,
                                            rate: "0",
                                            intercomArticleId: "11687982",
                                            i18nValue: i18n.t(
                                                "screens.user.salary_counting_details.thirteen_month_compensation",
                                            ),
                                            appliedMonthly: false,
                                        },
                                        {
                                            title: "public_holidays_compensation",
                                            quantity: 1,
                                            amount: salaryDetails.publicHolidaysCompensation,
                                            rate: "0",
                                            intercomArticleId: "11688025",
                                            i18nValue: i18n.t(
                                                "screens.user.salary_counting_details.public_holidays_compensation",
                                            ),
                                            appliedMonthly: false,
                                        },
                                        {
                                            title: "vacations_compensation",
                                            quantity: 1,
                                            amount: salaryDetails.vacationCompensation,
                                            rate: "0",
                                            intercomArticleId: "11688064",
                                            i18nValue: i18n.t(
                                                "screens.user.salary_counting_details.vacations_compensation",
                                            ),
                                            appliedMonthly: false,
                                        },
                                    ].map(formatItem)}
                                </VStack>
                            </ToggleView>
                        </>
                    )}
                    {route.params.earningType === EarningType.Monthly && (
                        <LabelValueText
                            label={i18n.t(
                                "screens.user.salary_counting_details.13th_compensations",
                            )}
                            value={currencyFormatter({
                                amountInCents: totalCompensation,
                            })}
                        />
                    )}
                    {/* Gross salary */}
                    <LabelValueText
                        label={i18n.t("screens.user.salary_counting_details.gross_salary")}
                        textStyle={fonts.bodyLarge}
                        value={currencyFormatter({
                            amountInCents: salaryDetails.grossSalary,
                        })}
                    />
                    <Gap gap={1.5} />
                    <TitleMediumText>
                        {i18n.t("screens.user.salary_counting_details.worker_salary_title")}
                    </TitleMediumText>
                    <LabelValueText
                        label={i18n.t("screens.user.salary_counting_details.gross_salary")}
                        value={currencyFormatter({
                            amountInCents: salaryDetails.grossSalary,
                        })}
                    />
                    {heldBack && (
                        <LabelValueText
                            label={i18n.t(
                                `screens.user.salary_counting_details.${heldBack.labelKey}`,
                            )}
                            value={currencyFormatter({ amountInCents: -heldBack.amount })}
                        />
                    )}
                    {/* Deduction Worker*/}
                    <>
                        <LabelValueText
                            label={i18n.t("screens.user.salary_counting_details.worker_deductions")}
                            value={currencyFormatter({
                                amountInCents: -totalWorkerDeductions,
                            })}
                        />
                        <ListItem
                            inGroup
                            style={styles.details_text}
                            labels={[
                                {
                                    text: i18n.t(
                                        `screens.user.salary_counting_details.${
                                            isWorkerDeductionsDetailsVisible ? "hide" : "show"
                                        }_details`,
                                    ),
                                    mode: "miniCta",
                                },
                            ]}
                            onPress={onWorkerDeductionsDetailsPress}
                            rightActionMode="none"
                        />
                        <ToggleView isOpen={isWorkerDeductionsDetailsVisible}>
                            <VStack gap="1/3">{displayedWorkerDeductions.map(formatItem)}</VStack>
                        </ToggleView>
                    </>
                    {/* With the indemnity held back, what is left is what the worker is paid this
                        period, which differs from the net under an LSE employer (see getPaidSalary). */}
                    <LabelValueText
                        label={i18n.t(
                            `screens.user.salary_counting_details.${
                                heldBack ? "paid_salary" : "net_salary"
                            }`,
                        )}
                        textStyle={fonts.bodyLarge}
                        value={currencyFormatter({
                            amountInCents: heldBack
                                ? getPaidSalary({
                                      netSalary: salaryDetails.netSalary,
                                      heldBackIndemnity: heldBack.amount,
                                      deferredIndemnity: salaryDetails.deferredIndemnity,
                                  })
                                : salaryDetails.netSalary,
                        })}
                    />
                    {/* The provision taken out above is paid to the worker on release. */}
                    {heldBack && (
                        <BodySmallText color={colors.greyAccessible}>
                            {i18n.t(
                                `screens.user.salary_counting_details.${heldBack.workerNoteKey}`,
                            )}
                        </BodySmallText>
                    )}
                    {workerMonthlyDeductions.length > 0 && (
                        <>
                            <Gap gap={1} />
                            <BodySmallText color={colors.greyAccessible}>
                                {monthlyDeductionsSentence(
                                    "screens.user.salary_counting_details.worker_monthly_deductions_info",
                                    workerMonthlyDeductions.map((d) => ({
                                        label: d.i18nValue || "?",
                                        amount: d.amount,
                                    })),
                                )}
                            </BodySmallText>
                        </>
                    )}
                    <Gap gap={1.5} />
                    <TitleMediumText>
                        {i18n.t("screens.user.salary_counting_details.client_salary_title")}
                    </TitleMediumText>
                    <LabelValueText
                        label={i18n.t("screens.user.salary_counting_details.gross_salary")}
                        value={currencyFormatter({
                            amountInCents: salaryDetails.grossSalary,
                        })}
                    />
                    {isClientPayingThePeriod && heldBack && (
                        <LabelValueText
                            label={i18n.t(
                                `screens.user.salary_counting_details.${heldBack.labelKey}`,
                            )}
                            value={currencyFormatter({ amountInCents: -heldBack.amount })}
                        />
                    )}
                    {/* Deduction Client*/}
                    <>
                        <LabelValueText
                            label={i18n.t("screens.user.salary_counting_details.client_deductions")}
                            value={currencyFormatter({
                                amountInCents: totalClientDeductions,
                            })}
                        />
                        <ListItem
                            inGroup
                            style={styles.details_text}
                            labels={[
                                {
                                    text: i18n.t(
                                        `screens.user.salary_counting_details.${
                                            isClientDeductionsDetailsVisible ? "hide" : "show"
                                        }_details`,
                                    ),
                                    mode: "miniCta",
                                },
                            ]}
                            onPress={onClientDeductionsDetailsPress}
                            rightActionMode="none"
                        />
                        <ToggleView isOpen={isClientDeductionsDetailsVisible}>
                            <VStack gap="1/3">{displayedClientDeductions.map(formatItem)}</VStack>
                        </ToggleView>
                    </>
                    {isClientPayingThePeriod ? (
                        <>
                            <LabelValueText
                                textStyle={fonts.bodyLarge}
                                label={i18n.t(
                                    `screens.user.salary_counting_details.employer_period_total.${route.params.earningType}`,
                                )}
                                value={currencyFormatter({
                                    amountInCents:
                                        salaryDetails.grossSalary -
                                        (heldBack?.amount ?? 0) +
                                        totalClientDeductions,
                                })}
                            />
                            {/* The provision left out above is paid on release: the client pays it
                                then, e.g. while the worker is on vacation. */}
                            {heldBack && (
                                <BodySmallText color={colors.greyAccessible}>
                                    {i18n.t(
                                        `screens.user.salary_counting_details.${heldBack.employerNoteKey}`,
                                    )}
                                </BodySmallText>
                            )}
                        </>
                    ) : (
                        <>
                            <LabelValueText
                                textStyle={fonts.bodyLarge}
                                label={i18n.t(
                                    "screens.user.salary_counting_details.total_with_client_deductions",
                                )}
                                value={currencyFormatter({
                                    amountInCents: salaryDetails.invoiceSalary,
                                })}
                            />
                            {profileType === ProfileType.Client && !!vatToUse && (
                                <LabelValueText
                                    label={i18n.t("screens.user.salary_counting_details.taxes", {
                                        percent: vatToUse,
                                    })}
                                    value={currencyFormatter({
                                        amountInCents: Math.floor(
                                            (salaryDetails.invoiceSalary * vatToUse) / 100,
                                        ),
                                    })}
                                />
                            )}
                            {profileType === ProfileType.Client && (
                                <LabelValueText
                                    textStyle={fonts.bodyLarge}
                                    label={i18n.t(
                                        "screens.user.salary_counting_details.invoiced_to_client",
                                    )}
                                    value={currencyFormatter({
                                        amountInCents: Math.floor(
                                            salaryDetails.invoiceSalary * (1 + vatToUse / 100),
                                        ),
                                    })}
                                />
                            )}
                        </>
                    )}
                    {(profileType === ProfileType.Client || clientMonthlyDeductions.length > 0) && (
                        <>
                            <Gap gap={1} />
                            <BodySmallText color={colors.greyAccessible}>
                                {monthlyDeductionsSentence(
                                    "screens.user.salary_counting_details.client_monthly_deductions_info",
                                    [
                                        ...(profileType === ProfileType.Client
                                            ? [
                                                  {
                                                      label: i18n.t(
                                                          "screens.user.salary_counting_details.lift_monthly_fee",
                                                      ),
                                                      amount: LIFT_MONTHLY_FEE_PER_EMPLOYEE,
                                                  },
                                              ]
                                            : []),
                                        ...clientMonthlyDeductions.map((d) => ({
                                            label: d.i18nValue || "?",
                                            amount: d.amount,
                                        })),
                                    ],
                                )}
                            </BodySmallText>
                        </>
                    )}
                </VStack>
            </ScreenCard>
        </>
    );
};

const useStyles = () => {
    const { marginSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                details_text: {
                    marginHorizontal: -marginSize,
                    paddingTop: 0,
                },
            }),
        [marginSize],
    );
};

export default SalaryCountingDetailsScreen;
